import React, { useEffect, useMemo } from 'react'
import {
  Modal, Form, Input, InputNumber, Select, AutoComplete,
  Row, Col, Divider, Typography,
} from 'antd'
import { useCostItemOptions } from '@/hooks/useCostItems'
import type { WorkOrderCostItem, CostItemCreate, CostItemUpdate } from '@/types/costItems'

const { Text } = Typography

const DEBITORE_LABELS: Record<string, string> = {
  cliente: 'Cliente',
  assicurazione: 'Assicurazione',
}

function roundHalfUp(v: number, decimals: number): number {
  const factor = Math.pow(10, decimals)
  return Math.round((v + Number.EPSILON) * factor) / factor
}

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: (data: CostItemCreate | CostItemUpdate) => void
  loading: boolean
  costItem?: WorkOrderCostItem
}

const CostoModal: React.FC<Props> = ({ open, onClose, onConfirm, loading, costItem }) => {
  const [form] = Form.useForm()
  const isEditing = !!costItem
  const { data: options } = useCostItemOptions()

  const quantita = Form.useWatch('quantita', form) as number | undefined
  const prezzo   = Form.useWatch('prezzo_unitario', form) as number | undefined
  const sconto   = Form.useWatch('sconto_percentuale', form) as number | undefined
  const iva      = Form.useWatch('aliquota_iva', form) as number | undefined
  const attiva   = Form.useWatch('attiva', form) as boolean | undefined

  useEffect(() => {
    if (!open) {
      form.resetFields()
      return
    }
    form.resetFields()
    if (costItem) {
      form.setFieldsValue({
        descrizione:         costItem.descrizione,
        quantita:            Number(costItem.quantita),
        unita_misura:        costItem.unita_misura ?? undefined,
        prezzo_unitario:     Number(costItem.prezzo_unitario),
        sconto_percentuale:  Number(costItem.sconto_percentuale),
        aliquota_iva:        Number(costItem.aliquota_iva),
        debitore:            costItem.debitore,
        note:                costItem.note ?? undefined,
        attiva:              costItem.attiva,
        motivo_esclusione:   costItem.motivo_esclusione ?? undefined,
      })
    } else {
      form.setFieldsValue({
        sconto_percentuale: 0,
        aliquota_iva:       options?.aliquota_iva_default ?? 22,
        debitore:           'cliente',
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Aggiorna aliquota_iva default quando le options arrivano (apertura creazione)
  useEffect(() => {
    if (!open || costItem || options == null) return
    if (form.getFieldValue('aliquota_iva') == null) {
      form.setFieldValue('aliquota_iva', options.aliquota_iva_default)
    }
  }, [options, open, costItem, form])

  const preview = useMemo(() => {
    const q = quantita ?? 0
    const p = prezzo ?? 0
    const s = (sconto ?? 0) / 100
    const a = (iva ?? 0) / 100
    const imponibile = roundHalfUp(q * p * (1 - s), 2)
    const ivaVal     = roundHalfUp(imponibile * a, 2)
    return { imponibile, iva: ivaVal, totale: imponibile + ivaVal }
  }, [quantita, prezzo, sconto, iva])

  const handleOk = async () => {
    try {
      const v = await form.validateFields()
      const base: CostItemCreate = {
        descrizione:        v.descrizione,
        quantita:           v.quantita,
        unita_misura:       v.unita_misura ?? null,
        prezzo_unitario:    v.prezzo_unitario,
        sconto_percentuale: v.sconto_percentuale ?? 0,
        aliquota_iva:       v.aliquota_iva,
        debitore:           v.debitore,
        note:               v.note ?? null,
      }
      if (isEditing) {
        onConfirm({
          ...base,
          attiva:            v.attiva,
          motivo_esclusione: v.motivo_esclusione ?? null,
        } as CostItemUpdate)
      } else {
        onConfirm(base)
      }
    } catch {
      // errori inline del form
    }
  }

  const unitaMisuraOpts = (options?.unita_misura_comuni ?? []).map(v => ({ value: v, label: v }))
  const debitoriOpt     = (options?.debitori ?? []).map(v => ({ value: v, label: DEBITORE_LABELS[v] ?? v }))

  return (
    <Modal
      open={open}
      title={isEditing ? 'Modifica riga di costo' : 'Aggiungi riga di costo'}
      okText={isEditing ? 'Salva' : 'Aggiungi'}
      cancelText="Annulla"
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={loading}
      destroyOnClose
      width={520}
    >
      <Form form={form} layout="vertical" size="small">
        <Form.Item
          name="descrizione"
          label="Descrizione"
          rules={[{ required: true, message: 'Obbligatoria' }]}
        >
          <Input placeholder="Es. Sostituzione pastiglie freno" />
        </Form.Item>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="quantita"
              label="Quantità"
              rules={[
                { required: true, message: 'Obbligatoria' },
                {
                  validator: (_, v) =>
                    v !== 0
                      ? Promise.resolve()
                      : Promise.reject(new Error('Non può essere zero')),
                },
              ]}
            >
              <InputNumber precision={2} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="unita_misura" label="U.M.">
              <AutoComplete
                options={unitaMisuraOpts}
                placeholder="pz, ore..."
                filterOption={(inputValue, option) =>
                  option!.value.toLowerCase().includes(inputValue.toLowerCase())
                }
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="prezzo_unitario"
              label="Prezzo unitario (€)"
              rules={[
                { required: true, message: 'Obbligatorio' },
                { type: 'number', min: 0, message: 'Non può essere negativo' },
              ]}
            >
              <InputNumber min={0} precision={2} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="sconto_percentuale" label="Sconto (%)">
              <InputNumber min={0} max={100} precision={2} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="aliquota_iva"
              label="IVA (%)"
              rules={[{ required: true, message: 'Obbligatoria' }]}
            >
              <InputNumber min={0} max={100} precision={2} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="debitore" label="Debitore">
              <Select options={debitoriOpt} />
            </Form.Item>
          </Col>
        </Row>

        {isEditing && (
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="attiva" label="Riga attiva">
                <Select
                  options={[
                    { value: true,  label: 'Sì — inclusa nel totale' },
                    { value: false, label: 'No — esclusa dal totale' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="motivo_esclusione" label="Motivo esclusione">
                <Input
                  disabled={attiva !== false}
                  placeholder={attiva === false ? 'Specifica il motivo' : 'Solo se esclusa'}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Form.Item name="note" label="Note" style={{ marginBottom: 8 }}>
          <Input.TextArea rows={2} />
        </Form.Item>

        <Divider style={{ margin: '8px 0' }} />
        <div style={{ display: 'flex', gap: 20, justifyContent: 'flex-end' }}>
          <span>
            <Text type="secondary" style={{ fontSize: 12 }}>Imponibile: </Text>
            <Text strong style={{ fontSize: 12 }}>{preview.imponibile.toFixed(2)} €</Text>
          </span>
          <span>
            <Text type="secondary" style={{ fontSize: 12 }}>IVA: </Text>
            <Text strong style={{ fontSize: 12 }}>{preview.iva.toFixed(2)} €</Text>
          </span>
          <span>
            <Text type="secondary" style={{ fontSize: 12 }}>Totale: </Text>
            <Text strong style={{ fontSize: 13, color: '#1677ff' }}>{preview.totale.toFixed(2)} €</Text>
          </span>
        </div>
      </Form>
    </Modal>
  )
}

export default CostoModal
